import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { REPORTS_V2 } from '../../../../shared/featureFlags/flagNames';
import { useParams } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features';
import ProjectGroupsModule from '../../../redux/project_grouping';
import { nuc, openInNewTab } from '../../../utils';
import { isReleaseGroupScope, type ReportsV3Params, type ReportV3Format } from '../types';

import { useReportOptionsParams } from './useReportOptionsParams';

/**
 * Hook that returns a callback to download a report by opening the download
 * endpoint in a new tab. The browser handles the file download via
 * Content-Disposition headers set by the server.
 *
 * Supports both project-level and release-group-level downloads.
 *
 * @param format The report format.
 */
export function useDownloadReport(format: ReportV3Format) {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const isReportsV2Enabled = useIsFeatureEnabled(REPORTS_V2);
  const apiVersion = isReportsV2Enabled ? '/v2' : '';
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);
  const reportOptionsParams = useReportOptionsParams();

  const url = useMemo(() => {
    if (isReleaseGroup) {
      const { projectGroupId, releaseId } = params;
      const resolvedReleaseId = releaseId ?? selectedReleaseId ?? 0;
      return `/api${apiVersion}/project_group/${projectGroupId}/release/${resolvedReleaseId}/attribution/${format}?download=true&format=${format}${reportOptionsParams ? `&${reportOptionsParams}` : ''}`;
    }
    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api${apiVersion}/revisions/${projectLocator}/attribution/download?download=true&format=${format}${reportOptionsParams ? `&${reportOptionsParams}` : ''}`;
  }, [apiVersion, format, isReleaseGroup, params, reportOptionsParams, selectedReleaseId]);

  const downloadReport = useCallback(() => {
    openInNewTab(url);
  }, [url]);

  return { downloadReport };
}
